import { useStore } from '@nanostores/react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { accountStore } from '@/services/auth/store';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import { getApiCredentialsForEnvironmentQueryOptions } from '@/services/db/api-credential/queries';
import { useIntegrationsWorkspaceId } from '../workspace-id';

/**
 * Newest credential first. This is the order the connector pages list
 * credentials in, so it belongs with the shared read rather than being
 * re-derived by each table.
 */
function newestFirst(credentials: ApiCredential[]): ApiCredential[] {
  return [...credentials].sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
}

/**
 * The current workspace's tracking API credentials.
 *
 * The account id arrives with the account, independently of the route, so the
 * query stays disabled until it does rather than firing against account `0`.
 */
export function useWorkspaceApiCredentials(): UseQueryResult<ApiCredential[]> {
  const workspaceId = useIntegrationsWorkspaceId();
  const account = useStore(accountStore);
  const accountId = account?.id != null ? Number(account.id) : undefined;

  return useQuery({
    ...getApiCredentialsForEnvironmentQueryOptions(accountId ?? 0, workspaceId),
    enabled: accountId != null,
    select: newestFirst,
  });
}
